import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import gcashLogo from "../assets/payment/gcash.png";
import mastercardLogo from "../assets/payment/mastercard.png";
import mayaLogo from "../assets/payment/maya.png";
import qrphLogo from "../assets/payment/qrph.png";
import shopeeLogo from "../assets/payment/shopee.png";
import visaLogo from "../assets/payment/visa.png";
import "../index.css";
import SeoManager from "../SeoManager";

const API_BASE = "https://ecomapi.asherjewels.com.ph/apiecom";
const paymentLogos = [
  { src: gcashLogo, alt: "GCash" },
  { src: mastercardLogo, alt: "Mastercard" },
  { src: mayaLogo, alt: "Maya" },
  { src: qrphLogo, alt: "QR Ph" },
  { src: shopeeLogo, alt: "ShopeePay" },
  { src: visaLogo, alt: "Visa" },
];

export default function CheckoutPage() {
  const { user } = useAuth();
  const { cartItems, removeFromCart } = useCart();
  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponState, setCouponState] = useState(null);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponMessageType, setCouponMessageType] = useState("info");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    postal_code: "",
    contact_number: "",
  });

  const SHIPPING_FEE = 0;
  const subtotal = cartItems.reduce((sum, item) => sum + Number(item.pricePHP || 0) * item.qty, 0);
  const couponDiscount = Number(couponState?.discount_amount || 0);
  const total = Math.max(0, subtotal - couponDiscount + SHIPPING_FEE);

  useEffect(() => {
    setCouponState(null);
    setCouponMessage("");
    setCouponMessageType("info");
  }, [subtotal, cartItems.length]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/get_shipping_details.php`, {
          credentials: "include",
        });
        const { status, data } = await resp.json();
        if (status === "success") {
          setAddresses(Array.isArray(data) ? data : []);
          setSelectedId(Array.isArray(data) && data.length > 0 ? data[0].id : null);
        }
      } catch (error) {
        console.error("Failed to load addresses", error);
      }
    })();
  }, [user]);

  const saveAddress = async (event) => {
    event.preventDefault();
    const endpoint = editingId ? "edit_shipping_details.php" : "save_shipping_details.php";
    const payload = {
      ...form,
      ...(editingId ? { id: editingId } : {}),
    };

    try {
      const resp = await fetch(`${API_BASE}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const result = await resp.json();

      if (result.status === "success") {
        const updatedList = editingId
          ? addresses.map((address) => (address.id === editingId ? { ...address, ...form } : address))
          : [...addresses, { id: result.shipping_id, ...form }];

        setAddresses(updatedList);
        setSelectedId(editingId || result.shipping_id);
        resetForm();
      } else {
        alert("Save failed: " + result.message);
      }
    } catch (error) {
      console.error("Network error while saving", error);
      alert("Network error while saving");
    }
  };

  const handlePlaceOrder = async () => {
    const selectedAddress = addresses.find((address) => address.id === selectedId);
    if (!selectedAddress) {
      alert(addresses.length ? "Please select a shipping address before proceeding to payment." : "Please add a shipping address before proceeding to payment.");
      if (!addresses.length) {
        setShowForm(true);
      }
      return;
    }

    const payload = {
      user_id: user.id,
      full_name: user.full_name,
      email: user.email,
      shipping_id: selectedId,
      shipping_details: selectedAddress,
      total_amount: total,
      coupon_code: couponState?.coupon_code || "",
      items: cartItems.map((item) => ({
        product_id: item.id,
        name: item.name,
        pricePHP: Number(item.pricePHP),
        qty: item.qty,
        product_code: item.product_code,
        imageUrl: item.imageUrl,
        ae_id: item.product_code,
      })),
    };

    try {
      const res = await fetch(`${API_BASE}/create_invoice.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.status === "success" && data.invoice_url) {
        window.location.href = data.invoice_url;
      } else {
        alert("Invoice creation failed: " + (data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Invoice error", error);
      alert("Network error occurred");
    }
  };

  const applyCoupon = async () => {
    const normalizedCode = couponCode.trim().toUpperCase();
    if (!normalizedCode) {
      setCouponMessage("Please enter a coupon code first.");
      setCouponMessageType("error");
      setCouponState(null);
      return;
    }

    setApplyingCoupon(true);
    setCouponMessage("");
    setCouponMessageType("info");

    try {
      const resp = await fetch(`${API_BASE}/validate_coupon.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          coupon_code: normalizedCode,
          subtotal,
        }),
      });
      const result = await resp.json();

      if (result.status === "success" && result.data) {
        setCouponState(result.data);
        setCouponCode(result.data.coupon_code || normalizedCode);
        setCouponMessage(`Coupon applied. You saved PHP ${formatCurrency(result.data.discount_amount || 0)}.`);
        setCouponMessageType("success");
      } else {
        setCouponState(null);
        setCouponMessage(result.message || "Unable to apply coupon.");
        setCouponMessageType("error");
      }
    } catch (error) {
      console.error("Coupon validation failed", error);
      setCouponState(null);
      setCouponMessage("Unable to validate coupon right now.");
      setCouponMessageType("error");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const clearCoupon = () => {
    setCouponState(null);
    setCouponCode("");
    setCouponMessage("");
    setCouponMessageType("info");
  };

  const deleteAddress = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      const resp = await fetch(`${API_BASE}/delete_shipping_details.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id }),
      });
      const result = await resp.json();
      if (result.status === "success") {
        const remaining = addresses.filter((address) => address.id !== id);
        setAddresses(remaining);
        if (selectedId === id) setSelectedId(remaining[0]?.id || null);
      } else {
        alert("Delete failed: " + result.message);
      }
    } catch (error) {
      console.error("Network error while deleting", error);
      alert("Network error while deleting");
    }
  };

  const startEdit = (address) => {
    setEditingId(address.id);
    setForm(address);
    setShowForm(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      name: "",
      address: "",
      city: "",
      postal_code: "",
      contact_number: "",
    });
    setShowForm(false);
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (cartItems.length === 0) {
    return <Navigate to="/" replace />;
  }

  const formatCurrency = (value) =>
    Number(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="container">
      <SeoManager
        title="Checkout"
        description="Secure checkout for your Asher Jewels PH order."
        path="/checkout"
        robots="noindex,nofollow"
      />
      <div className="checkout-container">
        <h1>Checkout</h1>

        <table className="checkout-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Item</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Total</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {cartItems.map((item) => (
              <tr key={item.id}>
                <td>
                  <img src={`https://onesystem.asherjewels.com.ph/${item.imageUrl}`} alt={item.name} width={50} />
                </td>
                <td>{item.name}</td>
                <td>{item.qty}</td>
                <td>PHP {formatCurrency(item.pricePHP)}</td>
                <td>PHP {formatCurrency(Number(item.pricePHP) * item.qty)}</td>
                <td>
                  <button className="btn-delete" onClick={() => removeFromCart(item.id)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="5">Subtotal</td>
              <td>PHP {formatCurrency(subtotal)}</td>
            </tr>
            <tr>
              <td colSpan="5">Coupon Discount</td>
              <td>- PHP {formatCurrency(couponDiscount)}</td>
            </tr>
            <tr>
              <td colSpan="5">Shipping Fee</td>
              <td>PHP {formatCurrency(SHIPPING_FEE)}</td>
            </tr>
            <tr>
              <td colSpan="5"><strong>Total</strong></td>
              <td><strong>PHP {formatCurrency(total)}</strong></td>
            </tr>
          </tfoot>
        </table>

        <section className="shipping-section checkout-coupon-section">
          <div className="flex-between">
            <h2>Discount Code</h2>
          </div>
          <p className="checkout-coupon-caption">Enter your coupon code to apply your discount before payment.</p>
          <div className="checkout-coupon-form">
            <input
              type="text"
              className="checkout-coupon-input"
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(event) => setCouponCode(event.target.value.toUpperCase())}
            />
            <button type="button" className="checkout-coupon-button" onClick={applyCoupon} disabled={applyingCoupon}>
              {applyingCoupon ? "Applying..." : "Apply"}
            </button>
            {couponState ? (
              <button type="button" className="checkout-coupon-clear" onClick={clearCoupon}>
                Remove
              </button>
            ) : null}
          </div>
          {couponMessage ? <p className={`checkout-helper-text checkout-helper-text--${couponMessageType}`}>{couponMessage}</p> : null}
        </section>

        <section className="shipping-section checkout-payment-methods">
          <div className="flex-between">
            <h2>Supported Payment Methods</h2>
          </div>
          <p className="checkout-coupon-caption">Choose from our supported payment channels once you proceed to payment.</p>
          <div className="payment-logo-grid" aria-label="Supported payment methods">
            {paymentLogos.map((logo) => (
              <div key={logo.alt} className="payment-logo-card">
                <img src={logo.src} alt={logo.alt} className="payment-logo-image" />
              </div>
            ))}
          </div>
        </section>

        <section className="shipping-section">
          <div className="flex-between">
            <h2>Shipping Address</h2>
            {!showForm && (
              <button className="btn-add" onClick={() => setShowForm(true)}>
                Add Address
              </button>
            )}
          </div>

          {!showForm && addresses.map((address) => (
            <div key={address.id} className={`address-block ${selectedId === address.id ? "active" : ""}`}>
              <input type="radio" checked={selectedId === address.id} onChange={() => setSelectedId(address.id)} />
              <div className="address-details">
                <p><strong>{address.name}</strong></p>
                <p>{address.address}, {address.city}, {address.postal_code}</p>
                <p>{address.contact_number}</p>
              </div>
              <button className="btn-edit" onClick={() => startEdit(address)}>Edit</button>
              <button className="btn-delete" onClick={() => deleteAddress(address.id)}>Delete</button>
            </div>
          ))}

          {showForm && (
            <form onSubmit={saveAddress} className="shipping-form">
              <input placeholder="Full Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
              <input placeholder="Address" value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} required />
              <input placeholder="City" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} required />
              <input placeholder="Postal Code" value={form.postal_code} onChange={(event) => setForm({ ...form, postal_code: event.target.value })} required />
              <input placeholder="Contact Number" value={form.contact_number} onChange={(event) => setForm({ ...form, contact_number: event.target.value })} required />
              <div className="shipping-form-actions">
                <button type="submit" className="btn-save">{editingId ? "Update Address" : "Save Address"}</button>
                <button type="button" className="btn-cancel" onClick={resetForm}>Cancel</button>
              </div>
            </form>
          )}
        </section>

        <div className="checkout-actions">
          <button
            className={`gold-button ${!addresses.length || !selectedId ? "is-disabled" : ""}`}
            onClick={handlePlaceOrder}
            disabled={!addresses.length || !selectedId}
            aria-disabled={!addresses.length || !selectedId}
          >
            Proceed to Payment
          </button>
          {!addresses.length ? (
            <p className="checkout-helper-text">Add a shipping address first before proceeding to payment.</p>
          ) : !selectedId ? (
            <p className="checkout-helper-text">Select a shipping address before proceeding to payment.</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

